import {
  buildChildrensBookPhysicalPageItems,
  childrensBookWriterPageLabel,
} from "./childrensBookPhysicalPagination";

import {
  childrensBookIllustrationsByUnit,
  currentChildrensBookCover,
} from "./childrensBookAssetState";

import {
  plainTextFromManuscriptEntry,
} from "./childrensBookManuscriptContent";

function clean(value) {
  return String(
    value ?? ""
  ).trim();
}

export function unitRequiresText(
  unit
) {
  return (
    clean(
      unit?.textAmount
    ).toLowerCase() !== "none"
  );
}

export function unitRequiresIllustration(
  unit
) {
  const requirement =
    clean(
      unit?.illustrationPlan
    );

  return Boolean(
    requirement &&
    requirement !== "none"
  );
}

export function childrensBookUnitReference(
  unit,
  index = 0,
  lang = "en"
) {
  const isFR =
    String(lang).toLowerCase() ===
    "fr";

  const position =
    clean(
      unit?.pageNumber
    ) ||
    String(index + 1);

  if (
    unit?.unitType ===
    "double_page_spread"
  ) {
    return isFR
      ? `Double page ${position}`
      : `Spread ${position}`;
  }

  return `Page ${position}`;
}

function unitIssue({
  unit,
  index,
  lang,
  physicalPageItems,
  kind,
  reasonEN,
  reasonFR,
}) {
  const isFR =
    String(lang).toLowerCase() ===
    "fr";

  const physicalItem =
    Array.isArray(
      physicalPageItems
    )
      ? physicalPageItems[
          index
        ] || null
      : null;

  const physicalReference =
    childrensBookWriterPageLabel(
      physicalItem
    );

  const reference =
    physicalReference ||
    childrensBookUnitReference(
      unit,
      index,
      lang
    );

  const multiplePages =
    Boolean(
      physicalItem &&
      Number(
        physicalItem.pageEnd
      ) >
        Number(
          physicalItem.pageStart
        )
    );

  return {
    unitId:
      clean(unit?.id),
    kind,
    reference,
    message: isFR
      ? (
          multiplePages
            ? `${reference} nécessitent votre attention : ${reasonFR}`
            : `${reference} nécessite votre attention : ${reasonFR}`
        )
      : (
          multiplePages
            ? `${reference} need attention: ${reasonEN}`
            : `${reference} needs attention: ${reasonEN}`
        ),
  };
}

function coverIssue(lang) {
  const isFR =
    String(lang).toLowerCase() ===
    "fr";

  return {
    unitId: "",
    kind:
      "missing_active_cover",
    reference: isFR
      ? "Couverture"
      : "Cover",
    message: isFR
      ? "Couverture nécessite votre attention : aucune couverture active n’est jointe."
      : "Cover needs attention: an active cover has not been attached.",
  };
}

export function joinChildrensBookDiagnosticMessages(
  items = []
) {
  const seen =
    new Set();

  return items
    .map(
      (item) =>
        clean(
          item?.message
        )
    )
    .filter(Boolean)
    .filter(
      (message) => {
        if (
          seen.has(
            message
          )
        ) {
          return false;
        }

        seen.add(
          message
        );

        return true;
      }
    )
    .join(" ");
}


export function countChildrensBookDiagnosticTargets(
  items = []
) {
  const targets =
    new Set();

  (
    Array.isArray(items)
      ? items
      : []
  ).forEach(
    (item, index) => {
      const unitId =
        clean(
          item?.unitId
        );

      const reference =
        clean(
          item?.reference
        );

      const message =
        clean(
          item?.message
        );

      const key =
        unitId
          ? `unit:${unitId}`
          : reference
            ? `reference:${reference}`
            : message
              ? `message:${message}`
              : `item:${index}`;

      targets.add(
        key
      );
    }
  );

  return targets.size;
}


export function summarizeChildrensBookDiagnostics(
  items = [],
  {
    lang = "en",
    context = "general",
  } = {}
) {
  const count =
    countChildrensBookDiagnosticTargets(
      items
    );

  if (!count) {
    return "";
  }

  const isFR =
    String(lang)
      .toLowerCase() ===
    "fr";

  if (context === "manuscript") {
    if (isFR) {
      return count === 1
        ? "1 page ou double page nécessite du travail sur le manuscrit."
        : `${count} pages ou doubles pages nécessitent du travail sur le manuscrit.`;
    }

    return count === 1
      ? "1 page or spread needs manuscript work."
      : `${count} pages or spreads need manuscript work.`;
  }

  if (context === "illustration") {
    if (isFR) {
      return count === 1
        ? "1 page ou double page nécessite du travail sur les illustrations."
        : `${count} pages ou doubles pages nécessitent du travail sur les illustrations.`;
    }

    return count === 1
      ? "1 page or spread needs illustration work."
      : `${count} pages or spreads need illustration work.`;
  }

  if (context === "assembly") {
    if (isFR) {
      return count === 1
        ? "1 élément du livre nécessite une vérification avant la révision."
        : `${count} éléments du livre nécessitent une vérification avant la révision.`;
    }

    return count === 1
      ? "1 book item needs attention before review."
      : `${count} book items need attention before review.`;
  }

  if (context === "publication") {
    if (isFR) {
      return count === 1
        ? "1 élément du livre nécessite une vérification avant la publication."
        : `${count} éléments du livre nécessitent une vérification avant la publication.`;
    }

    return count === 1
      ? "1 book item needs attention before publication."
      : `${count} book items need attention before publication.`;
  }

  if (isFR) {
    return count === 1
      ? "1 élément du livre nécessite votre attention."
      : `${count} éléments du livre nécessitent votre attention.`;
  }

  return count === 1
    ? "1 book item needs attention."
    : `${count} book items need attention.`;
}


export function buildChildrensBookUnitDiagnostics(
  projectPlan = {},
  childrensBookAssets = [],
  options = {}
) {
  const lang =
    options.lang ||
    "en";

  const oversizedUnitIds =
    new Set(
      Array.isArray(
        options.oversizedUnitIds
      )
        ? options
            .oversizedUnitIds
            .map(clean)
            .filter(Boolean)
        : []
    );

  const units =
    Array.isArray(
      projectPlan.bookMap
        ?.units
    )
      ? projectPlan.bookMap
          .units
      : [];

  const physicalPageItems =
    buildChildrensBookPhysicalPageItems(
      units
    );

  const manuscriptEntries =
    Array.isArray(
      projectPlan
        .manuscriptPlan
        ?.entries
    )
      ? projectPlan
          .manuscriptPlan
          .entries
      : [];

  const illustrationEntries =
    Array.isArray(
      projectPlan
        .illustrationPlan
        ?.entries
    )
      ? projectPlan
          .illustrationPlan
          .entries
      : [];

  const manuscriptByUnit =
    manuscriptEntries.reduce(
      (result, entry) => {
        const unitId =
          clean(
            entry?.unitId
          );

        if (unitId) {
          result[
            unitId
          ] = entry;
        }

        return result;
      },
      {}
    );

  const completedIllustrationIds =
    new Set(
      illustrationEntries
        .filter(
          (entry) =>
            entry?.status ===
            "complete"
        )
        .map(
          (entry) =>
            clean(
              entry?.unitId
            )
        )
        .filter(Boolean)
    );

  const illustrationsByUnit =
    childrensBookIllustrationsByUnit(
      childrensBookAssets
    );

  const activeCover =
    currentChildrensBookCover(
      childrensBookAssets,
      "front_cover"
    );

  const manuscript = [];
  const illustrationPlan = [];
  const assembly = [];

  units.forEach(
    (unit, index) => {
      const unitId =
        clean(
          unit?.id
        );

      const manuscriptEntry =
        manuscriptByUnit[
          unitId
        ] || null;

      const manuscriptText =
        clean(
          plainTextFromManuscriptEntry(
            manuscriptEntry
          )
        );

      const requiresText =
        unitRequiresText(
          unit
        );

      const requiresIllustration =
        unitRequiresIllustration(
          unit
        );

      if (
        requiresText &&
        !manuscriptText
      ) {
        manuscript.push(
          unitIssue({
            physicalPageItems,
            unit,
            index,
            lang,
            kind:
              "missing_manuscript_text",
            reasonEN:
              "manuscript text is required.",
            reasonFR:
              "le texte du manuscrit est requis.",
          })
        );
      } else if (
        requiresText &&
        manuscriptEntry?.status !==
          "complete"
      ) {
        manuscript.push(
          unitIssue({
            physicalPageItems,
            unit,
            index,
            lang,
            kind:
              "manuscript_not_complete",
            reasonEN:
              "the manuscript is not marked complete.",
            reasonFR:
              "le manuscrit n’est pas marqué comme terminé.",
          })
        );
      }

      if (
        oversizedUnitIds.has(
          unitId
        )
      ) {
        manuscript.push(
          unitIssue({
            physicalPageItems,
            unit,
            index,
            lang,
            kind:
              "manuscript_too_long",
            reasonEN:
              "the manuscript contains too much text and needs to be divided.",
            reasonFR:
              "le manuscrit contient trop de texte et doit être divisé.",
          })
        );
      }

      if (
        requiresIllustration &&
        !completedIllustrationIds
          .has(unitId) &&
        !illustrationsByUnit[
          unitId
        ]
      ) {
        illustrationPlan.push(
          unitIssue({
            physicalPageItems,
            unit,
            index,
            lang,
            kind:
              "illustration_plan_incomplete",
            reasonEN:
              "the illustration plan is not complete.",
            reasonFR:
              "le plan de l’illustration n’est pas terminé.",
          })
        );
      }

      if (
        requiresText &&
        !manuscriptText
      ) {
        assembly.push(
          unitIssue({
            physicalPageItems,
            unit,
            index,
            lang,
            kind:
              "missing_assembly_text",
            reasonEN:
              "manuscript text is missing from the assembled book.",
            reasonFR:
              "le texte du manuscrit manque dans le livre assemblé.",
          })
        );
      }

      if (
        requiresIllustration &&
        !illustrationsByUnit[
          unitId
        ]
      ) {
        assembly.push(
          unitIssue({
            physicalPageItems,
            unit,
            index,
            lang,
            kind:
              "missing_illustration_asset",
            reasonEN:
              "the required illustration has not been attached.",
            reasonFR:
              "l’illustration requise n’a pas été jointe.",
          })
        );
      }
    }
  );

  return {
    manuscript,
    illustrationPlan,
    assembly,
    cover:
      activeCover
        ? []
        : [
            coverIssue(
              lang
            ),
          ],
  };
}
